/**
 * useWomensHealthAnalytics.js
 *
 * Analytics hook for the Assessment Wizard's Screen 11 ("Women's
 * Health"). Mirrors useMealScheduleAnalytics.js exactly — same GA4
 * wrapper via react-ga4, same IS_PRODUCTION guard, same dev
 * console.group/table logging.
 */

import { useCallback } from "react";
import ReactGA from "react-ga4";

const IS_PRODUCTION =
  process.env.NODE_ENV === "production" &&
  typeof window !== "undefined" &&
  window.location.hostname !== "localhost" &&
  window.location.hostname !== "127.0.0.1";

const send = (eventName, params = {}) => {
  const cleanParams = Object.fromEntries(
    Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== "")
  );

  if (IS_PRODUCTION) {
    ReactGA.event(eventName, cleanParams);
  } else {
    // eslint-disable-next-line no-console
    console.group(`%c[WomensHealthAnalytics] ${eventName}`, "color:#AD1457;font-weight:bold;");
    // eslint-disable-next-line no-console
    if (Object.keys(cleanParams).length > 0) console.table(cleanParams);
    // eslint-disable-next-line no-console
    else console.log("(no params)");
    // eslint-disable-next-line no-console
    console.groupEnd();
  }
};

const useWomensHealthAnalytics = () => {
  const trackStepView = useCallback((sessionId) => {
    send("womens_health_step_view", { session_id: sessionId, step: 11 });
  }, []);

  const trackConditionToggle = useCallback((sessionId, conditions) => {
    send("womens_health_condition_toggle", {
      session_id: sessionId,
      conditions: conditions.join(","),
    });
  }, []);

  const trackTrimesterSelect = useCallback((sessionId, trimester) => {
    send("womens_health_trimester_select", { session_id: sessionId, trimester });
  }, []);

  const trackGestationalDiabetesSelect = useCallback((sessionId, value) => {
    send("womens_health_gestational_diabetes_select", {
      session_id: sessionId,
      gestational_diabetes: value,
    });
  }, []);

  const trackStepNext = useCallback((sessionId, data = {}) => {
    send("womens_health_step_next", {
      session_id: sessionId,
      step: 11,
      conditions: (data.conditions || []).join(","),
      trimester: data.trimester,
      gestational_diabetes: data.gestationalDiabetes,
    });
  }, []);

  const trackStepPrevious = useCallback((sessionId) => {
    send("womens_health_step_previous", { session_id: sessionId, step: 11 });
  }, []);

  const trackValidationBlocked = useCallback((sessionId, errors = {}) => {
    send("womens_health_validation_blocked", {
      session_id: sessionId,
      missing_fields: Object.keys(errors).join(","),
    });
  }, []);

  return {
    trackStepView,
    trackConditionToggle,
    trackTrimesterSelect,
    trackGestationalDiabetesSelect,
    trackStepNext,
    trackStepPrevious,
    trackValidationBlocked,
  };
};

export default useWomensHealthAnalytics;
